import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { money, apiErr, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { CheckCircle2, XCircle, Truck, Loader2, Download } from "lucide-react";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";

const MAROON = "#7A1F2B";

export default function TrackQuote() {
  const { token } = useParams();
  const [q, setQ] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/track/${token}`).then((r) => setQ(r.data)).catch((e) => setErr(apiErr(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const act = async (kind) => {
    setBusy(true);
    try {
      await api.post(`/track/${token}/${kind}`);
      toast.success(kind === "approve" ? "Shipment booked!" : "Feedback sent to our team");
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (err) return <Center><div className="text-center"><XCircle className="h-10 w-10 text-destructive mx-auto mb-3" /><p className="text-muted-foreground">{err}</p></div></Center>;
  if (!q) return <Center><Loader2 className="h-8 w-8 animate-spin text-primary" /></Center>;

  const co = q.company || {};
  const confirmed = q.order_state === "confirmed_dispatched";
  const hasQuote = q.quote_status === "sent" || q.quote_status === "approved" || q.quote_status === "declined";
  const sc = q.schedule || {};
  const sched = [
    sc.packing && `Packing: ${sc.packing}`,
    sc.moving && `Moving: ${sc.moving}`,
    sc.delivery && `Delivery: ${sc.delivery}`,
  ].filter(Boolean).join("   ");

  return (
    <div className="min-h-screen bg-neutral-100 py-8 px-4">
      <div className="max-w-3xl mx-auto bg-white text-neutral-900 shadow-sm border border-neutral-200" data-testid="quote-document">
        {/* Letterhead — logo + company profile branding */}
        <div className="flex items-start justify-between gap-4 p-6 border-b-2" style={{ borderColor: MAROON }}>
          <Logo className="h-14" />
          <div className="text-right text-[10.5px] leading-snug text-neutral-600 max-w-[62%]">
            <div className="font-bold text-neutral-800 text-xs">{co.name}</div>
            {co.office && <div>{co.office}</div>}
            {co.phone && <div>{co.phone}</div>}
            {(co.email || co.web) && <div>{[co.email, co.web].filter(Boolean).join(" · ")}</div>}
          </div>
        </div>

        {/* Title */}
        <div className="px-6 pt-5">
          <h1 className="text-center text-2xl font-bold tracking-wide" style={{ color: MAROON }} data-testid="quote-title">QUOTATION</h1>
          <div className="h-px mt-2" style={{ backgroundColor: MAROON }} />
        </div>

        <div className="p-6 space-y-5 text-sm">
          {/* To + meta */}
          <div className="flex justify-between gap-4">
            <div className="flex gap-2">
              <span className="font-bold">To,</span>
              <div className="text-[13px]">
                <div className="font-medium">{q.party_name}</div>
                {q.customer_company && <div>{q.customer_company}</div>}
                {q.delivery_address && <div className="text-neutral-600">{q.delivery_address}</div>}
                <div className="text-neutral-600">Tel: {q.customer_phone}</div>
                {q.customer_email && <div className="text-neutral-600">Email: {q.customer_email}</div>}
              </div>
            </div>
            <div className="text-right text-xs shrink-0">
              <div className="font-bold" data-testid="quote-order-number">Quot. No. : {q.quo_serial || q.order_number}</div>
              <div className="font-bold">Date : {q.date}</div>
              <div className="mt-2 flex justify-end"><StatusBadge status={confirmed ? "confirmed_dispatched" : q.quote_status} /></div>
            </div>
          </div>

          {hasQuote ? (
            <>
              <p className="text-neutral-600 text-xs">Thank you for your enquiry. We are pleased to submit our quotation for your relocation as detailed below.</p>

              {/* From / To / Particular */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5 text-xs font-medium border-y border-neutral-200 py-2">
                <div>From : {q.from_line}</div>
                <div>To : {q.to_line}</div>
                <div>Particular : {q.particular}</div>
              </div>
              {sched && <div className="text-xs text-neutral-600 -mt-2">Schedule — {sched}</div>}

              {/* Service table (SR / DESCRIPTION / AMOUNT) */}
              <div className="border border-neutral-200" data-testid="quote-charges-table">
                <div className="grid grid-cols-[36px_1fr_110px] font-bold text-[11px] px-3 py-2" style={{ backgroundColor: "#F4F4F4", color: "#1A1A1A" }}>
                  <div>SR.</div><div>DESCRIPTION</div><div className="text-right">AMOUNT (INR)</div>
                </div>
                {(q.rows || []).map((r, i) => (
                  <div key={i} className="grid grid-cols-[36px_1fr_110px] px-3 py-2 text-xs border-t border-neutral-100">
                    <div className="text-neutral-500">{i + 1}</div>
                    <div>{r.description}</div>
                    <div className={`text-right font-mono-num ${r.by_party ? "text-neutral-500" : ""}`}>{r.by_party ? "BY PARTY" : money(r.amount)}</div>
                  </div>
                ))}
              </div>

              {/* Remarks */}
              {q.remarks && <div className="text-xs"><span className="font-bold">Remarks: </span><span className="text-neutral-600">{q.remarks}</span></div>}

              {/* Net total + amount in words */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="text-xs italic text-neutral-700" data-testid="quote-amount-words">{q.amount_in_words}.</div>
                <div className="flex items-center justify-between gap-6 text-white font-bold px-4 py-2 min-w-[240px]" style={{ backgroundColor: MAROON }}>
                  <span>NET TOTAL</span><span data-testid="quote-total" className="font-mono-num">{money(q.total_val)}</span>
                </div>
              </div>

              {/* Points to be noted */}
              {(q.points || []).length > 0 && (
                <div data-testid="quote-points">
                  <div className="font-bold text-xs mb-1"># Points to be noted :</div>
                  <ol className="list-decimal pl-5 space-y-0.5 text-[11px] text-neutral-600">
                    {q.points.map((p, i) => <li key={i}>{p}</li>)}
                  </ol>
                </div>
              )}

              {/* Signatures */}
              <div className="flex justify-between items-end pt-8 text-xs">
                <div className="border-t border-neutral-400 w-40 pt-1">Customer Name & Signature</div>
                <div className="text-right">
                  <div className="font-bold mb-8">For, {co.name}</div>
                  <div className="border-t border-neutral-400 w-40 pt-1 ml-auto">Authorised Signatory</div>
                </div>
              </div>

              <Button variant="outline" className="w-full rounded-none gap-2 font-bold uppercase tracking-wide" onClick={() => window.open(`${API}/track/${token}/pdf`, "_blank")} data-testid="download-pdf-btn"><Download className="h-4 w-4" /> Download Quotation PDF</Button>
            </>
          ) : (
            <div className="border border-neutral-200 bg-neutral-50 p-6 text-center text-sm text-neutral-500">
              <Truck className="h-8 w-8 mx-auto mb-2" style={{ color: MAROON }} />
              Your quote is being prepared. Our team will finalise pricing shortly — check back soon.
            </div>
          )}

          {confirmed && (
            <div className="border border-emerald-500/40 bg-emerald-50 text-emerald-700 p-4 flex items-center gap-3">
              <CheckCircle2 className="h-6 w-6" /><div><div className="font-bold">Shipment confirmed & booked</div><div className="text-sm opacity-80">Our team will reach out with pickup details shortly.</div></div>
            </div>
          )}

          {q.quote_status === "sent" && !confirmed && (
            <div className="grid grid-cols-2 gap-3 pt-1">
              <Button onClick={() => act("approve")} disabled={busy} data-testid="approve-btn" className="h-12 rounded-none font-bold uppercase tracking-wide gap-2 text-white hover:opacity-90" style={{ backgroundColor: MAROON }}><CheckCircle2 className="h-5 w-5" /> Approve & Book</Button>
              <Button onClick={() => act("decline")} disabled={busy} variant="outline" data-testid="decline-btn" className="h-12 rounded-none font-bold uppercase tracking-wide gap-2"><XCircle className="h-5 w-5" /> Request Changes</Button>
            </div>
          )}
          {q.quote_status === "declined" && !confirmed && <div className="text-sm text-center text-neutral-500">You requested changes. Our team will reach out with a revised quote.</div>}
        </div>

        {/* Footer — company profile branding */}
        <div className="px-6 py-4 border-t-2 text-[11px] leading-snug" style={{ borderColor: MAROON }}>
          <div className="font-bold text-sm" style={{ color: MAROON }}>{co.name}</div>
          {(co.gstin || co.pan) && <div className="text-neutral-600">GSTIN : {co.gstin}{co.pan ? `  |  PAN : ${co.pan}` : ""}</div>}
          {co.regd && <div className="text-neutral-600">{co.regd}</div>}
          {(co.iso || co.tagline) && <div className="text-neutral-500 mt-1">{[co.iso, co.tagline].filter(Boolean).join(" · ")}</div>}
        </div>
      </div>
    </div>
  );
}

const Center = ({ children }) => <div className="min-h-screen grid place-items-center bg-background">{children}</div>;
